import React, { useState, useEffect } from 'react';
import {
    Layout, Card, Table, Button, Modal, Form, Input,
    DatePicker, Select, Tabs, message, Space, Popconfirm,
    Typography, Row, Col, Statistic, Divider
} from 'antd';
import {
    PlusOutlined, PrinterOutlined, EditOutlined,
    DeleteOutlined, ReloadOutlined, SearchOutlined,
    ImportOutlined
} from '@ant-design/icons';

import dayjs from 'dayjs';
import api from '../../utils/api';
import { generateDirectorAdvancePDF } from '../../services/directorAdvancePdfService';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const DirectorAdvancePage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('transactions');

    // Data States
    const [directors, setDirectors] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter States
    const [selectedDirector, setSelectedDirector] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs().startOf('year'), dayjs().endOf('year')]);

    // Modal States
    const [isTransModalVisible, setIsTransModalVisible] = useState(false);
    const [isDirectorModalVisible, setIsDirectorModalVisible] = useState(false);
    const [isAccountModalVisible, setIsAccountModalVisible] = useState(false);
    const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [editingItem, setEditingItem] = useState(null);

    const [form] = Form.useForm();
    const [directorForm] = Form.useForm();
    const [accountForm] = Form.useForm();

    // Fetch Initial Data
    useEffect(() => {
        fetchDirectors();
        fetchAccounts();
    }, []);

    // Fetch Transactions when filters change
    useEffect(() => {
        if (selectedDirector) {
            fetchTransactions();
        } else {
            setTransactions([]);
        }
    }, [selectedDirector, dateRange]);

    const fetchDirectors = async () => {
        try {
            const res = await api.get('/api/director-advance/directors');
            if (res.data.success) {
                setDirectors(res.data.data);
                if (res.data.data.length > 0 && !selectedDirector) {
                    setSelectedDirector(res.data.data[0].id);
                }
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลกรรมการได้');
        }
    };

    const fetchAccounts = async () => {
        try {
            const res = await api.get('/api/director-advance/accounts');
            if (res.data.success) {
                setAccounts(res.data.data);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลผังบัญชีได้');
        }
    };

    const fetchTransactions = async () => {
        if (!selectedDirector) return;
        setLoading(true);
        try {
            const params = {};
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.startDate = dateRange[0].format('YYYY-MM-DD');
                params.endDate = dateRange[1].format('YYYY-MM-DD');
            }
            const res = await api.get(`/api/director-advance/transactions/${selectedDirector}`, { params });
            if (res.data.success) {
                setTransactions(res.data.data);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลรายการได้');
        } finally {
            setLoading(false);
        }
    };

    // Transaction Handlers
    const handleTransSubmit = async (values) => {
        try {
            const payload = {
                ...values,
                trans_date: values.trans_date.format('YYYY-MM-DD'),
                director_id: selectedDirector
            };

            if (editingItem) {
                await api.put(`/api/director-advance/transactions/${editingItem.id}`, payload);
                message.success('แก้ไขรายการเรียบร้อย');
            } else {
                await api.post('/api/director-advance/transactions', payload);
                message.success('สร้างรายการเรียบร้อย');
            }
            setIsTransModalVisible(false);
            form.resetFields();
            setEditingItem(null);
            fetchTransactions();
        } catch (error) {
            message.error('ทำรายการไม่สำเร็จ');
        }
    };

    const handleDeleteTrans = async (id) => {
        try {
            await api.delete(`/api/director-advance/transactions/${id}`);
            message.success('ลบรายการเรียบร้อย');
            fetchTransactions();
        } catch (error) {
            message.error('ลบรายการไม่สำเร็จ');
        }
    };

    // Director Handlers
    const handleDirectorSubmit = async (values) => {
        try {
            if (editingItem) {
                await api.put(`/api/director-advance/directors/${editingItem.id}`, values);
                message.success('แก้ไขข้อมูลกรรมการเรียบร้อย');
            } else {
                await api.post('/api/director-advance/directors', values);
                message.success('เพิ่มกรรมการเรียบร้อย');
            }
            setIsDirectorModalVisible(false);
            directorForm.resetFields();
            setEditingItem(null);
            fetchDirectors();
        } catch (error) {
            message.error('ทำรายการไม่สำเร็จ');
        }
    };

    const handleDeleteDirector = async (id) => {
        try {
            await api.delete(`/api/director-advance/directors/${id}`);
            message.success('ลบข้อมูลกรรมการเรียบร้อย');
            fetchDirectors();
        } catch (error) {
            message.error('ลบข้อมูลไม่สำเร็จ');
        }
    };

    // Account Handlers
    const handleAccountSubmit = async (values) => {
        try {
            if (editingItem) {
                await api.put(`/api/director-advance/accounts/${editingItem.id}`, values);
                message.success('แก้ไขข้อมูลบัญชีเรียบร้อย');
            } else {
                await api.post('/api/director-advance/accounts', values);
                message.success('เพิ่มบัญชีเรียบร้อย');
            }
            setIsAccountModalVisible(false);
            accountForm.resetFields();
            setEditingItem(null);
            fetchAccounts();
        } catch (error) {
            message.error('ทำรายการไม่สำเร็จ');
        }
    };

    const handleDeleteAccount = async (id) => {
        try {
            await api.delete(`/api/director-advance/accounts/${id}`);
            message.success('ลบข้อมูลบัญชีเรียบร้อย');
            fetchAccounts();
        } catch (error) {
            message.error('ลบข้อมูลไม่สำเร็จ');
        }
    };

    // Columns
    const transactionColumns = [
        {
            title: 'วันที่',
            dataIndex: 'trans_date',
            key: 'trans_date',
            render: (text) => dayjs(text).format('DD/MM/YYYY'),
        },
        {
            title: 'เลขที่เอกสาร',
            dataIndex: 'ref_no',
            key: 'ref_no',
        },
        {
            title: 'รายการ',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'เดบิต (รับเงิน)',
            dataIndex: 'debit',
            key: 'debit',
            align: 'right',
            render: (val) => val > 0 ? val.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-',
            className: 'text-green-600'
        },
        {
            title: 'เครดิต (เคลียร์/คืน)',
            dataIndex: 'credit',
            key: 'credit',
            align: 'right',
            render: (val) => val > 0 ? val.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-',
            className: 'text-red-600'
        },
        {
            title: 'คงเหลือ',
            dataIndex: 'balance',
            key: 'balance',
            align: 'right',
            render: (val) => <Text strong>{val.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
        },
        {
            title: 'จัดการ',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => {
                        setEditingItem(record);
                        form.setFieldsValue({
                            ...record,
                            trans_date: dayjs(record.trans_date),
                            type: record.debit > 0 ? 'advance' : ((record.description && record.description.includes('คืน')) ? 'return' : 'clear'),
                            amount: record.debit > 0 ? record.debit : record.credit
                        });
                        setIsTransModalVisible(true);
                    }} />
                    <Popconfirm title="ยืนยันการลบ?" onConfirm={() => handleDeleteTrans(record.id)} okText="ใช่" cancelText="ไม่">
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const directorColumns = [
        { title: 'ชื่อ-นามสกุล', dataIndex: 'name', key: 'name' },
        { title: 'ตำแหน่ง', dataIndex: 'position', key: 'position' },
        {
            title: 'จัดการ',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => {
                        setEditingItem(record);
                        directorForm.setFieldsValue(record);
                        setIsDirectorModalVisible(true);
                    }} />
                    <Popconfirm title="ยืนยันการลบ?" onConfirm={() => handleDeleteDirector(record.id)} okText="ใช่" cancelText="ไม่">
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const accountColumns = [
        { title: 'รหัสบัญชี', dataIndex: 'code', key: 'code' },
        { title: 'ชื่อบัญชี', dataIndex: 'name', key: 'name' },
        {
            title: 'ประเภท',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                const types = {
                    asset: 'สินทรัพย์',
                    liability: 'หนี้สิน',
                    equity: 'ส่วนของเจ้าของ',
                    income: 'รายได้',
                    expense: 'ค่าใช้จ่าย'
                };
                return types[type] || type;
            }
        },
        {
            title: 'จัดการ',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => {
                        setEditingItem(record);
                        accountForm.setFieldsValue(record);
                        setIsAccountModalVisible(true);
                    }} />
                    <Popconfirm title="ยืนยันการลบ?" onConfirm={() => handleDeleteAccount(record.id)} okText="ใช่" cancelText="ไม่">
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const handlePrint = async () => {
        if (!selectedDirector) return;

        try {
            // Fetch necessary data for report
            const director = directors.find(d => d.id === selectedDirector);

            // Fetch company info (mock or API)
            let company = { name: 'บริษัท อะตอม ซอฟท์ จำกัด', tax_id: '0105561000000' };
            try {
                const companyRes = await api.get('/api/settings/company');
                if (companyRes.data.success) company = companyRes.data.data;
            } catch (e) {
                console.warn('Could not fetch company info, using default');
            }

            const params = {};
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.startDate = dateRange[0].format('YYYY-MM-DD');
                params.endDate = dateRange[1].format('YYYY-MM-DD');
            }

            const res = await api.get(`/api/director-advance/transactions/${selectedDirector}`, { params });

            if (res.data.success) {
                const url = await generateDirectorAdvancePDF(director, res.data.data, dateRange, company);
                setPdfUrl(url);
                setIsPdfModalVisible(true);
            }

        } catch (error) {
            console.error(error);
            message.error('ไม่สามารถสร้างรายงาน PDF ได้');
        }
    };

    return (
        <Layout style={{ padding: '24px' }}>
            <Title level={2}>จัดการเงินทดรองจ่ายกรรมการ</Title>

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <Tabs.TabPane tab="รายการเคลื่อนไหว" key="transactions">
                    <Card>
                        <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
                            <Col span={6}>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="เลือกกรรมการ"
                                    value={selectedDirector}
                                    onChange={setSelectedDirector}
                                >
                                    {directors.map(d => (
                                        <Option key={d.id} value={d.id}>{d.name}</Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col span={10}>
                                <Row gutter={8}>
                                    <Col span={15}>
                                        <RangePicker
                                            style={{ width: '100%' }}
                                            value={dateRange}
                                            onChange={setDateRange}
                                            format="DD/MM/YYYY"
                                        />
                                    </Col>
                                    <Col span={9}>
                                        <DatePicker
                                            picker="month"
                                            placeholder="เลือกเดือน"
                                            style={{ width: '100%' }}
                                            allowClear
                                            onChange={(date) => {
                                                if (date) {
                                                    setDateRange([date.startOf('month'), date.endOf('month')]);
                                                } else {
                                                    setDateRange(null);
                                                }
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                            <Col span={8} style={{ textAlign: 'right' }}>
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            setEditingItem(null);
                                            form.resetFields();
                                            form.setFieldsValue({ trans_date: dayjs() });
                                            setIsTransModalVisible(true);
                                        }}
                                        disabled={!selectedDirector}
                                    >
                                        สร้างรายการใหม่
                                    </Button>
                                    <Button
                                        icon={<ImportOutlined />}
                                        onClick={() => navigate('/bank-statement-import')}
                                    >
                                        นำเข้า Statement
                                    </Button>
                                    <Button
                                        icon={<PrinterOutlined />}
                                        onClick={handlePrint}
                                        disabled={!selectedDirector}
                                    >
                                        พิมพ์รายงาน
                                    </Button>
                                </Space>
                            </Col>
                        </Row>

                        <Table
                            columns={transactionColumns}
                            dataSource={transactions}
                            rowKey="id"
                            loading={loading}
                            pagination={{ pageSize: 20 }}
                            summary={pageData => {
                                let totalDebit = 0;
                                let totalCredit = 0;
                                pageData.forEach(({ debit, credit }) => {
                                    totalDebit += debit;
                                    totalCredit += credit;
                                });
                                return (
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={3}>รวมทั้งสิ้น</Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right">
                                            <Text type="success">{totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} align="right">
                                            <Text type="danger">{totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={3} align="right">
                                            <Text strong>{(totalDebit - totalCredit).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                );
                            }}
                        />
                    </Card>
                </Tabs.TabPane>

                <Tabs.TabPane tab="ข้อมูลกรรมการ" key="directors">
                    <Card>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            style={{ marginBottom: 16 }}
                            onClick={() => {
                                setEditingItem(null);
                                directorForm.resetFields();
                                setIsDirectorModalVisible(true);
                            }}
                        >
                            เพิ่มกรรมการ
                        </Button>
                        <Table
                            columns={directorColumns}
                            dataSource={directors}
                            rowKey="id"
                        />
                    </Card>
                </Tabs.TabPane>

                <Tabs.TabPane tab="จัดการผังบัญชี" key="accounts">
                    <Card>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            style={{ marginBottom: 16 }}
                            onClick={() => {
                                setEditingItem(null);
                                accountForm.resetFields();
                                setIsAccountModalVisible(true);
                            }}
                        >
                            เพิ่มผังบัญชี
                        </Button>
                        <Table
                            columns={accountColumns}
                            dataSource={accounts}
                            rowKey="id"
                        />
                    </Card>
                </Tabs.TabPane>
            </Tabs>

            {/* Transaction Modal */}
            <Modal
                title={editingItem ? "แก้ไขรายการ" : "สร้างรายการใหม่"}
                open={isTransModalVisible}
                onCancel={() => setIsTransModalVisible(false)}
                onOk={() => form.submit()}
                okText="บันทึก"
                cancelText="ยกเลิก"
            >
                <Form form={form} layout="vertical" onFinish={handleTransSubmit}>
                    <Form.Item name="trans_date" label="วันที่" rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                    <Form.Item name="type" label="ประเภทรายการ" rules={[{ required: true, message: 'กรุณาเลือกประเภทรายการ' }]}>
                        <Select>
                            <Option value="advance">ถอนเงินทดรอง (Advance)</Option>
                            <Option value="clear">เคลียร์ค่าใช้จ่าย (Clear Expense)</Option>
                            <Option value="return">คืนเงินบริษัท (Return Cash)</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="amount" label="จำนวนเงิน" rules={[{ required: true, message: 'กรุณาระบุจำนวนเงิน' }]}>
                        <Input type="number" step="0.01" />
                    </Form.Item>
                    <Form.Item name="ref_no" label="เลขที่เอกสาร">
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="รายละเอียด">
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item name="related_account_id" label="บัญชีที่เกี่ยวข้อง">
                        <Select showSearch optionFilterProp="children" placeholder="เลือกบัญชี">
                            {accounts.map(acc => (
                                <Option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Director Modal */}
            <Modal
                title={editingItem ? "แก้ไขข้อมูลกรรมการ" : "เพิ่มกรรมการใหม่"}
                open={isDirectorModalVisible}
                onCancel={() => setIsDirectorModalVisible(false)}
                onOk={() => directorForm.submit()}
                okText="บันทึก"
                cancelText="ยกเลิก"
            >
                <Form form={directorForm} layout="vertical" onFinish={handleDirectorSubmit}>
                    <Form.Item name="name" label="ชื่อ-นามสกุล" rules={[{ required: true, message: 'กรุณาระบุชื่อ' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="position" label="ตำแหน่ง">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Account Modal */}
            <Modal
                title={editingItem ? "แก้ไขผังบัญชี" : "เพิ่มผังบัญชีใหม่"}
                open={isAccountModalVisible}
                onCancel={() => setIsAccountModalVisible(false)}
                onOk={() => accountForm.submit()}
                okText="บันทึก"
                cancelText="ยกเลิก"
            >
                <Form form={accountForm} layout="vertical" onFinish={handleAccountSubmit}>
                    <Form.Item name="code" label="รหัสบัญชี" rules={[{ required: true, message: 'กรุณาระบุรหัสบัญชี' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="name" label="ชื่อบัญชี" rules={[{ required: true, message: 'กรุณาระบุชื่อบัญชี' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="type" label="ประเภทบัญชี" rules={[{ required: true, message: 'กรุณาเลือกประเภทบัญชี' }]}>
                        <Select>
                            <Option value="asset">สินทรัพย์ (Asset)</Option>
                            <Option value="liability">หนี้สิน (Liability)</Option>
                            <Option value="equity">ส่วนของเจ้าของ (Equity)</Option>
                            <Option value="income">รายได้ (Income)</Option>
                            <Option value="expense">ค่าใช้จ่าย (Expense)</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* PDF Preview Modal */}
            <Modal
                title="ตัวอย่างรายงาน (PDF Preview)"
                open={isPdfModalVisible}
                onCancel={() => setIsPdfModalVisible(false)}
                width={1000}
                footer={[
                    <Button key="close" onClick={() => setIsPdfModalVisible(false)}>
                        ปิด
                    </Button>
                ]}
                style={{ top: 20 }}
            >
                {pdfUrl && (
                    <iframe
                        src={pdfUrl}
                        style={{ width: '100%', height: '80vh', border: 'none' }}
                        title="PDF Preview"
                    />
                )}
            </Modal>
        </Layout>
    );
};

export default DirectorAdvancePage;
